import { type ApiFetchContext, apiFetch } from "../client";
import { ApiError } from "../errors";
import type {
  InboxMessage,
  InboxMessageRaw,
  InboxPaged,
  InboxPagedRaw,
  InboxUnread,
  InboxUnreadRawEnvelope,
} from "../types/inbox";

function normalize(raw: InboxMessageRaw): InboxMessage {
  return {
    messageId: raw.messageId,
    sendDateTime: raw.sendDateTime ?? "",
    subject: raw.subject ?? "",
    messageImg: raw.messageImg ?? "",
    senderName: raw.senderName ?? "",
    messageText: raw.messageText ?? "",
    messageType: raw.messageType ?? 0,
    orderNo: raw.orderNo ?? "",
    expressOrderNo: raw.expressOrderNo ?? "",
  };
}

/**
 * GET /inboxMessages/list — paged inbox or unread set.
 *
 * Backend dispatches on messageType:
 *   - messageType === 1 → redis UNREAD-SET branch, returns
 *     `{unReadCount, inboxMessages}` (NO `records`). Use getInboxUnread for that.
 *   - anything else     → paged DB query of the full mailbox (`records`).
 * For the full mailbox, pass a non-1 messageType (e.g. 0). Do NOT omit it:
 * `messageType` is a boxed Integer and `messageType == 1` unboxes server-side,
 * so a null value throws NPE → `{code:500,"system error"}`.
 * Note: backend uses `pageNum` (not pageNo) — mind the typo.
 */
export async function listInboxMessages(
  ctx: ApiFetchContext,
  authToken: string,
  input: { pageNum: number; pageSize: number; messageType?: number },
): Promise<InboxPaged> {
  const parts = [
    `pageNum=${input.pageNum}`,
    `pageSize=${input.pageSize}`,
    ...(input.messageType != null ? [`messageType=${input.messageType}`] : []),
  ];
  const raw = await apiFetch<InboxPagedRaw>(ctx, `/inboxMessages/list?${parts.join("&")}`, {
    authToken,
  });
  return {
    records: (raw.records ?? []).map(normalize),
    total: raw.total ?? 0,
    pageNo: raw.current ?? input.pageNum,
    pageSize: raw.size ?? input.pageSize,
    pages: raw.pages ?? 1,
  };
}

/** GET /inboxMessages/list?messageType=1 — unread set + count. */
export async function getInboxUnread(
  ctx: ApiFetchContext,
  authToken: string,
): Promise<InboxUnread> {
  // Backend dispatches by messageType=1 to redis branch; pageNum/pageSize
  // are required path params but ignored, send 1/1 to keep URL tight.
  const raw = await apiFetch<InboxUnreadRawEnvelope>(
    ctx,
    "/inboxMessages/list?pageNum=1&pageSize=1&messageType=1",
    { authToken },
  );
  return {
    unReadCount: raw.unReadCount ?? 0,
    messages: (raw.inboxMessages ?? []).map(normalize),
  };
}

export async function getInboxUnreadCount(
  ctx: ApiFetchContext,
  authToken: string,
): Promise<number> {
  return apiFetch<number>(ctx, "/inboxMessages/getUnReadCount", { authToken });
}

export async function popUpInboxMessage(
  ctx: ApiFetchContext,
  authToken: string,
): Promise<InboxMessage | null> {
  // popUp legitimately returns null when redis queue is empty — apiFetch's
  // null-payload guard would throw BAD_ENVELOPE, so swallow that specific
  // code here.
  try {
    const raw = await apiFetch<InboxMessageRaw>(ctx, "/inboxMessages/popUp", {
      authToken,
    });
    return normalize(raw);
  } catch (e) {
    if (e instanceof ApiError && e.code === "BAD_ENVELOPE") return null;
    throw e;
  }
}

export async function processInboxMessage(
  ctx: ApiFetchContext,
  authToken: string,
  message: InboxMessageRaw,
): Promise<void> {
  await apiFetch<unknown>(ctx, "/inboxMessages/process", {
    method: "POST",
    body: message,
    authToken,
  });
}
